import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Group } from '@mantine/core'
import { ChatLayout } from '../components/layout/ChatLayout'
import { WelcomeContent } from '../features/chat/components/WelcomeContent'
import { ChatInputSimple } from '../features/chat/components/ChatInputSimple'
import { useAutoFocus } from '../hooks'
import { ConversationContent } from '../features/chat/components/ConversationContent'
import { generateConversationId } from '../utils/uuid'
import { useChatStore, useChatSelectors } from '../stores/chatStore'
import { useUserInfo } from '../hooks/useUserInfo'
import { useConversationStore } from '../stores/conversationStore'
import { isTeReoLanguageWeek } from '../utils/teReoLanguageWeek'
import { agentsQueryOptions } from '../api/agents'
import { AGENT_IDS } from '../constants/agents'
import { consumePendingHomeDeepLink, type HomeDeepLink } from '../utils/homeDeepLinks'

export const Route = createFileRoute('/')({
  component: HomePage,
})

function HomePage() {
  const navigate = useNavigate()

  // Get user information for personalized greeting
  const userInfo = useUserInfo()
  const showTeReoGreeting = isTeReoLanguageWeek()
  const { data: agents = [], isFetching: isAgentsFetching, isFetchedAfterMount, isError: isAgentsError, refetch: refetchAgents } = useQuery({
    ...agentsQueryOptions(),
    refetchOnMount: 'always',
  })
  const [deepLink, setDeepLink] = useState<HomeDeepLink | null>(null)
  const consumedDeepLink = useRef<HomeDeepLink | null | undefined>(undefined)
  const [deepLinkApplied, setDeepLinkApplied] = useState(false)
  const [defaultSelectionConfirmed, setDefaultSelectionConfirmed] = useState(false)
  const [deepLinkNotice, setDeepLinkNotice] = useState<string | null>(null)

  useEffect(() => {
    if (consumedDeepLink.current === undefined) {
      consumedDeepLink.current = consumePendingHomeDeepLink()
    }
    setDeepLink(consumedDeepLink.current)
  }, [])

    // Get sidebar visibility from conversation store (AppShell uses this)
  const { isExpanded } = useConversationStore()

  // Use local state to prevent momentary shifts
  const [sidebarVisible, setSidebarVisible] = useState(true)

  // Synchronize with AppShell state
  useEffect(() => {
    setSidebarVisible(isExpanded)
  }, [isExpanded])

  // Generate new conversation ID on component mount
  const [newConversationId] = useState(() => generateConversationId());

  // Home screen conversation state
  const homeScreenConversation = useChatStore(state => state.homeScreenConversation);
  const hasHomeScreenConversation = useChatSelectors.hasHomeScreenConversation();
  const clearHomeScreenConversation = useChatStore(state => state.clearHomeScreenConversation);
  const clearAttachments = useChatStore(state => state.clearAttachments);

  useEffect(() => {
    if (!deepLink || deepLinkApplied || !isFetchedAfterMount || isAgentsFetching) return
    if (isAgentsError && !defaultSelectionConfirmed) return

    const selectedAgent = !isAgentsError && !defaultSelectionConfirmed && deepLink.agent === AGENT_IDS.DIGITAL_MAINTENANCE
      ? agents.find(agent => agent.id === AGENT_IDS.DIGITAL_MAINTENANCE)
      : undefined;
    const setInputValue = useChatStore.getState().setInputValue;
    const setSelectedAgents = useChatStore.getState().setSelectedAgents;

    if (deepLink.prompt && !deepLink.promptError) {
      setInputValue(newConversationId, deepLink.prompt);
    }

    if (selectedAgent) {
      setSelectedAgents(newConversationId, [AGENT_IDS.DIGITAL_MAINTENANCE]);
    } else {
      setSelectedAgents(newConversationId, []);
      if (!isAgentsError && !defaultSelectionConfirmed && deepLink.agent === AGENT_IDS.DIGITAL_MAINTENANCE) {
        setDeepLinkNotice('Digital Maintenance is not available for your account. The default assistant will be used.');
      }
    }

    setDeepLinkApplied(true);
  }, [agents, deepLink, deepLinkApplied, defaultSelectionConfirmed, isAgentsError, isAgentsFetching, isFetchedAfterMount, newConversationId]);

  const confirmDefaultSelection = useCallback(() => {
    setDefaultSelectionConfirmed(true);
    setDeepLinkNotice('The default assistant is selected.');
  }, []);

  const deepLinkCatalogBlocked = Boolean(deepLink && !deepLinkApplied);
  const deepLinkCatalogError = Boolean(deepLinkCatalogBlocked && isAgentsError && !defaultSelectionConfirmed && !isAgentsFetching);

  // Clear home screen conversation and attachments when component mounts (navigating back to home)
  useEffect(() => {
    clearHomeScreenConversation();
    clearAttachments();
  }, [clearHomeScreenConversation, clearAttachments]);


  // Auto-focus the chat input when the welcome screen loads
  const chatInputRef = useAutoFocus<HTMLTextAreaElement>({
    condition: !hasHomeScreenConversation,
    delay: true // Use requestAnimationFrame for smooth UX
  });


  const handlePromptClick = useCallback((text: string) => {
    // Use the same pattern as the prompt library modal
    // Get the setInputValue action from the chat store
    const setInputValue = useChatStore.getState().setInputValue;

    // Populate the chat input with the selected prompt text
    setInputValue(newConversationId, text);

    console.log('Quick start prompt selected:', text);
  }, [newConversationId]);

  // Navigate to conversation after successful message send (for now, we'll remove this)
  const handleMessageSent = useCallback(() => {
    // Don't navigate immediately - let optimistic UI handle it
  }, [])

  // Convert home screen conversation to messages format
  const homeScreenMessages = homeScreenConversation ? [
    {
      ...homeScreenConversation.userMessage,
      id: homeScreenConversation.userMessage.id,
      role: homeScreenConversation.userMessage.role,
      content: homeScreenConversation.userMessage.content,
      timestamp: homeScreenConversation.userMessage.timestamp,
    },
    ...(homeScreenConversation.assistantMessage ? [{
      ...homeScreenConversation.assistantMessage,
      id: homeScreenConversation.assistantMessage.id,
      role: homeScreenConversation.assistantMessage.role,
      content: homeScreenConversation.assistantMessage.content,
      timestamp: homeScreenConversation.assistantMessage.timestamp,
    }] : [])
  ] : [];


  return (
    <ChatLayout
      footerContent={
        <ChatInputSimple
          ref={chatInputRef}
          conversationId={newConversationId}
          disabled={deepLinkCatalogBlocked}
          showControls={true}
          onMessageSent={handleMessageSent}
        />
      }
    >
      {!hasHomeScreenConversation && deepLink && (deepLink.promptError || deepLink.agentError || deepLinkCatalogError) && (
        <Alert color="red" title="This chat link needs attention" mb="md">
          {deepLink.promptError}
          {deepLink.promptError && deepLink.agentError ? ' ' : ''}
          {deepLink.agentError}
          {deepLinkCatalogError && (
            <Group mt="sm" gap="sm">
              <Button size="xs" onClick={confirmDefaultSelection}>Use default assistant</Button>
              <Button size="xs" variant="subtle" onClick={() => void refetchAgents()}>Retry agent list</Button>
            </Group>
          )}
        </Alert>
      )}
      {!hasHomeScreenConversation && deepLinkNotice && (
        <Alert color="yellow" title="Agent selection notice" mb="md">
          {deepLinkNotice}
        </Alert>
      )}
      {hasHomeScreenConversation && !homeScreenConversation?.error ?(
        <ConversationContent
          conversationId={homeScreenConversation!.id}
          messages={homeScreenMessages}
          loading={false}
          error={homeScreenConversation?.error}
          isHomeScreen={true}
        />
      ) : (
        <WelcomeContent
          userName={userInfo.displayName}
          onPromptClick={handlePromptClick}
          isSidebarVisible={sidebarVisible}
          titleOverride={showTeReoGreeting
            ? `Tēnā koe ${userInfo.displayName}, kei te hiahia āwhina koe?`
            : undefined}
          description={showTeReoGreeting
            ? `Hello ${userInfo.displayName}, would you like some help?`
            : undefined}
        />
      )}
    </ChatLayout>
  );
}
